/**
 * THE TWO QUALITY RATIOS AND THE DWELL P50, ON OVERVIEW.
 *
 * Not a summary of the quality panel — a SELECTION from it. The resolution rate
 * and the auto-close rate are the two numbers that change how much the rest of
 * the screen can be trusted:
 *
 *   · a resolution rate of 4% means the roster links behind every "who was this
 *     visitor for" answer barely exist, and no count anywhere else says so;
 *   · a high auto-close rate means the departures on this screen were stamped by
 *     the nightly sweep rather than watched by anybody.
 *
 * The other four are on Trends, and this links there rather than duplicating
 * them. A null renders as an absence with its own sentence, never as 0%.
 */
import React from 'react';
import { AnalyticsLatency, AnalyticsQuality } from '../../../../../api/analytics-client';
import { PanelShell, ChartShellState } from '../charts/chart-shell';
import { PanelToggle } from '../charts/chart.styled';
import { format } from '../charts/chart-theme';
import {
  FigureCell,
  FigureGrid,
  FigureLabel,
  FigureNote,
  FigureUnit,
  FigureValue,
  MeterMeta,
  StatePill,
} from './panels.styled';

const HEADLINE = [
  {
    key: 'resolution_rate',
    label: 'Resolution rate',
    note: 'How many entries are linked to a real occupancy. Low means the "who was this for" answers behind every other figure are guesses.',
  },
  {
    key: 'auto_close_rate',
    label: 'Auto-close rate',
    note: 'LOW is good. An auto-close is a desk-process gap, not a departure: the nightly sweep stamped 23:59 because nobody signed the visit out.',
  },
];

export interface QualityHeadlineProps {
  state: ChartShellState;
  quality?: AnalyticsQuality;
  latency?: AnalyticsLatency;
  latencyLoading: boolean;
  onSeeAll: () => void;
}

export const QualityHeadline: React.FC<QualityHeadlineProps> = ({
  state,
  quality,
  latency,
  latencyLoading,
  onSeeAll,
}) => (
  <PanelShell
    id="quality-headline"
    title="How reliable is this?"
    state={state}
    caveat="Two of the six data-quality ratios, and the typical wait. These are about the RECORD, not about the building — they say how much the rest of this screen can be trusted."
    controls={
      <PanelToggle type="button" onClick={onSeeAll} data-testid="see-all-quality">
        All six on Trends
      </PanelToggle>
    }
    emptyReason={
      quality && !quality.ratios.length
        ? {
            title: 'No ratios were returned',
            text: 'The server answered without any quality ratios, so there is nothing to show and nothing to infer.',
          }
        : null
    }
  >
    {quality ? (
      <FigureGrid>
        {HEADLINE.map(headline => {
          const ratio = quality.ratios.find(r => r.key === headline.key);
          if (!ratio) return null;
          const absent = ratio.ratio == null;

          return (
            <FigureCell key={headline.key} data-testid={`headline-${headline.key}`}>
              <FigureLabel>{headline.label}</FigureLabel>
              <FigureValue>
                {/* NULL IS NOT ZERO. The server sends null for a zero denominator
                    on purpose: 0% reads as "nothing was scanned" when the truth
                    is "nothing was recorded at all". */}
                {absent ? '—' : format.ratio(ratio.ratio)}
                {absent ? null : (
                  <FigureUnit>
                    {format.fraction(ratio.numerator, ratio.denominator)}
                  </FigureUnit>
                )}
              </FigureValue>
              <FigureNote>
                {absent
                  ? 'Nothing in this period makes this measurable, so there is no ratio rather than a ratio of zero.'
                  : headline.note}
              </FigureNote>
            </FigureCell>
          );
        })}

        <FigureCell data-testid="headline-dwell">
          <FigureLabel>Typical wait (p50)</FigureLabel>
          <FigureValue>
            {latencyLoading
              ? '…'
              : latency?.hoursView?.p50 == null
              ? '—'
              : (latency.hoursView?.p50 as number).toLocaleString()}
            <FigureUnit>{latency?.hoursView?.p50 == null ? '' : 'hours'}</FigureUnit>
          </FigureValue>
          <FigureNote>
            {/* NEVER "average". One parcel forgotten for six weeks moves a mean by
                days and says nothing about what the typical resident
                experienced. */}
            A percentile, not an average — half of the {latency?.noun?.[1] || 'items'}{' '}
            that closed waited less than this.
          </FigureNote>
        </FigureCell>
      </FigureGrid>
    ) : null}

    {latency && latency.overThreshold > 0 ? (
      <MeterMeta>
        <StatePill tone="warning">
          {latency.overThreshold.toLocaleString()} open right now and already past{' '}
          {latency.thresholdHours}h
        </StatePill>
        <span>
          That list is not filtered by the period above — it is what is open at this
          moment.
        </span>
      </MeterMeta>
    ) : null}
  </PanelShell>
);
